import { useEffect, useMemo, useRef, useState } from 'react';

import { usePaymentContext } from 'features/payment/quick-pay';
import { IPaymentMethod } from 'features/payment/types';
import {
  getPaymentMethodId,
  isAllowedQuickPayMethod,
  isCreditCardNotExpired,
} from 'features/payment/utils';

import { useLoyaltyUpdateIdAndPayMutation, useLoyaltyUserQuery } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import logger from 'utils/logger';

// Validating expiration date for the current credit card
const isCurrentPaymentMethodValid = (
  availablePaymentMethods: IPaymentMethod[],
  selectedPaymentMethod?: string | null
) => {
  const currentPayment = availablePaymentMethods.find(
    item => getPaymentMethodId(item) === selectedPaymentMethod
  );

  return !!currentPayment && isCreditCardNotExpired(currentPayment?.credit);
};

export const useQuickPayState = () => {
  const {
    paymentMethods,
    recentlyAddedQuickPayMethodId,
    setRecentlyAddedQuickPayMethodId,
  } = usePaymentContext();

  const availablePaymentMethods = useMemo(
    () => paymentMethods.filter(paymentMethod => isAllowedQuickPayMethod(paymentMethod)),
    [paymentMethods]
  );

  // Ref to check if the component is initialized, we are going to use it to
  // allow the user to toggle the quick pay toggle button
  const isInitialized = useRef(false);
  // True after one missing-`idPay` seed mutation
  const emptyIdPayInitRef = useRef(false);
  const [selectedMethod, setSelectedMethod] = useState<string | undefined>();
  const { isQuickPayEnabled, setIsQuickPayEnabled } = useInRestaurantRedemptionContext();

  const { loyaltyUserId: loyaltyId } = useLoyaltyUserId();
  const { user } = useAuthContext();

  const { data, loading } = useLoyaltyUserQuery({
    skip: !loyaltyId,
    variables: { loyaltyId },
    fetchPolicy: 'network-only',
    // TO DO: Make an action on error...
  });

  const idPay = data?.loyaltyUserV2?.idPay;
  const isQuickPayAvailable = availablePaymentMethods.length > 0 && !!idPay;
  const [loyaltyQuickPayMutation] = useLoyaltyUpdateIdAndPayMutation();

  useEffect(() => {
    if (isInitialized.current || !idPay) {
      return;
    }
    const isSelectedMethodAvailable = availablePaymentMethods.some(
      method => getPaymentMethodId(method) === idPay.storedPaymentMethodId
    );

    if (idPay.enabled && isSelectedMethodAvailable) {
      setIsQuickPayEnabled(true);
    }
    if (idPay.storedPaymentMethodId === undefined || idPay.storedPaymentMethodId === null) {
      setSelectedMethod(
        availablePaymentMethods.length
          ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
          : undefined
      );
      // The user had a payment method selected and it's not available anymore
    } else if (!!idPay.storedPaymentMethodId && !isSelectedMethodAvailable) {
      setSelectedMethod(
        availablePaymentMethods.length
          ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
          : undefined
      );
      setIsQuickPayEnabled(false);
    } else {
      setSelectedMethod(idPay.storedPaymentMethodId);
    }
    isInitialized.current = true;
  }, [idPay, availablePaymentMethods, setIsQuickPayEnabled]);

  useEffect(() => {
    if (!recentlyAddedQuickPayMethodId) {
      return;
    }

    const addedMethodIsAvailable = availablePaymentMethods.some(
      method => getPaymentMethodId(method) === recentlyAddedQuickPayMethodId
    );

    if (!addedMethodIsAvailable) {
      return;
    }

    setSelectedMethod(recentlyAddedQuickPayMethodId);
    setIsQuickPayEnabled(true);
    setRecentlyAddedQuickPayMethodId(undefined);
  }, [
    availablePaymentMethods,
    recentlyAddedQuickPayMethodId,
    setIsQuickPayEnabled,
    setRecentlyAddedQuickPayMethodId,
  ]);

  useEffect(() => {
    if (!idPay) {
      // No Id & Pay row on the loyalty user yet: persist a default so the API returns `idPay`.
      // We use the most recently added eligible card with quick pay off; the init effect above
      // can then align local toggle/selection once `idPay` exists.
      if (
        !emptyIdPayInitRef.current &&
        loyaltyId &&
        !loading &&
        data?.loyaltyUserV2 &&
        availablePaymentMethods.length > 0
      ) {
        emptyIdPayInitRef.current = true;
        loyaltyQuickPayMutation({
          variables: {
            idAndPayInput: {
              storedPaymentMethodId: availablePaymentMethods.length
                ? getPaymentMethodId(availablePaymentMethods[availablePaymentMethods.length - 1])
                : undefined,
              enabled: false,
            },
            loyaltyId,
          },
          refetchQueries: ['LoyaltyUser'],
        });
      }
      return;
    }
    if (
      (idPay.storedPaymentMethodId !== selectedMethod || idPay.enabled !== isQuickPayEnabled) &&
      isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)
    ) {
      loyaltyQuickPayMutation({
        variables: {
          idAndPayInput: { storedPaymentMethodId: selectedMethod, enabled: isQuickPayEnabled },
          loyaltyId,
        },
        refetchQueries: ['LoyaltyUser'],
      });
    }
    if (!isQuickPayAvailable && idPay.storedPaymentMethodId) {
      loyaltyQuickPayMutation({
        variables: {
          idAndPayInput: { storedPaymentMethodId: undefined, enabled: false },
          loyaltyId,
        },
        refetchQueries: ['LoyaltyUser'],
      });
    }
  }, [
    idPay,
    selectedMethod,
    availablePaymentMethods,
    loyaltyQuickPayMutation,
    loyaltyId,
    loading,
    data?.loyaltyUserV2,
    isQuickPayEnabled,
    isQuickPayAvailable,
  ]);

  const handleToggle = () => {
    if (isCurrentPaymentMethodValid(availablePaymentMethods, selectedMethod)) {
      logger.info({
        message: `[INFO] Quick Pay Toggle`,
        loyaltyId: user?.loyaltyId,
        toggleStatus: !isQuickPayEnabled,
      });
    }
    setIsQuickPayEnabled(isEnabled => !isEnabled);
  };

  const handleSelection = (fdAccountId: string | undefined) => {
    setSelectedMethod(fdAccountId);
  };

  return {
    isQuickPayEnabled,
    selectedMethod,
    availablePaymentMethods,
    isQuickPayAvailable,
    data,
    loading,
    loyaltyQuickPayMutation,
    handleToggle,
    handleSelection,
  };
};
